import React from 'react';
import PropTypes from 'prop-types';

import { WeezHeader, Card, ToastContext } from '@weezevent/nacre';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AttendeesManagement from './AttendeesManagement';
import IdentityInvitation from '../../models/identityInvitation';
import PartnerIdentity from './partnerIdentity';
import OrganizationMediaList from './organizationMediaList';
import OrganizationColor, { OrganizationColorHidden } from './organizationColor';
import OrganizationSettings from '../../models/organizationSettings';

export default function PartnerSettings({ organization }) {
    const history = useHistory();
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [steps, setSteps] = React.useState({ 1: 'done', 2: 'done' });
    const [identity, setIdentity] = React.useState({ from_address: { name: null, email: 'invitation@weezevent.com' }, reply_to: { name: null, email: null } });
    const [settings, loading, syncSettings] = OrganizationSettings.useApiModel({
        organizationId: organization.id,
        onError: e => {
            if (e.code !== 404) {
                toast.error(t('ticket.partners.get.mediaError'));
            }
        }
    });
    const [color, setColor] = React.useState(null);

    const setStep = React.useCallback(
        _steps => {
            setSteps({
                ...steps,
                ..._steps
            });
        },
        [steps]
    );

    React.useEffect(() => {
        IdentityInvitation.getAll({ organizationId: organization.id })
            .then(response => {
                setIdentity(response);
            })
            .catch(e => {
                setIdentity({ from_address: { name: null, email: 'invitation@weezevent.com' }, reply_to: { name: null, email: null } });
            });
    }, []);

    React.useEffect(() => {
        if (settings) {
            setColor(settings?.color);
        }
    }, [settings]);

    const onSubmitIdentity = React.useCallback(
        from_identity => {
            let _identity = new IdentityInvitation(identity);
            if (_identity.inherited) {
                delete _identity.id;
            }
            _identity['from_address'] = { name: from_identity.from_address_name, email: from_identity.from_address_email };
            _identity['reply_to'] = { name: from_identity.reply_to_name, email: from_identity.reply_to_email };
            _identity
                .save({ organizationId: organization.id })
                .then(({ response }) => {
                    setStep({ 1: 'done' });
                    setIdentity(response);
                    toast.success(t('ticket.partners.settings.identity.create_update_successfully'));
                })
                .catch(e => {
                    toast.error(t('ticket.partners.settings.identity.create_update_error'));
                });
        },
        [identity]
    );

    return (
        <React.Fragment>
            <WeezHeader title={t('ticket.partners.settings.title')} backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/partners`) }} />

            <Card.FormStep
                title={t('ticket.partners.settings.identity.title')}
                state={steps[1]}
                onCardEdit={() => setStep({ 1: 'open' })}
                editLabel={t('ticket.common.action.edit')}
                subtitle={t('ticket.partners.settings.identity.description')}
                summary={
                    identity.from_address.name !== null ? (
                        <>
                            {`${t('ticket.partners.settings.identity.from')}: ${identity.from_address.name} < ${identity.from_address.email} >`} <br />
                            {`${t('ticket.partners.settings.identity.to')}: ${identity.reply_to.name} < ${identity.reply_to.email} >`}
                        </>
                    ) : (
                        t('ticket.partners.settings.identity.default_identity')
                    )
                }
            >
                <PartnerIdentity formState={identity} onSubmit={onSubmitIdentity} onCancel={() => setStep({ 1: 'done' })} />
            </Card.FormStep>
            {!loading && (
                <>
                    <Card.FormStep
                        title={t('ticket.partners.color.title')}
                        state={steps[2]}
                        onCardEdit={() => setStep({ 2: 'open' })}
                        editLabel={t('ticket.common.action.edit')}
                        summary={<OrganizationColorHidden color={color || settings?.color} />}
                    >
                        <OrganizationColor
                            organization={organization}
                            color={settings?.color}
                            onSubmit={color => {
                                setColor(color);
                                setStep({ 2: 'done' });
                            }}
                        />
                    </Card.FormStep>
                    <Card title={t('ticket.common.action.customize')} style={{ padding: '15px' }}>
                        <OrganizationMediaList organization={organization} medias={settings?.medias || []} />
                    </Card>
                    <AttendeesManagement organization={organization} attendees_management={settings?.attendees_management} sync={syncSettings} />
                </>
            )}
        </React.Fragment>
    );
}

PartnerSettings.propTypes = {
    organization: PropTypes.object
};
